import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query'
import { useRef } from 'react'

import {
  IMAGE_DIRECT_UPLOAD_THRESHOLD_BYTES,
  type MediaSort,
} from '@brickup/shared/constants'
import type {
  CreateMediaCollectionDto,
  ImageUsageGeometry,
  ImageUsageResponse,
  MediaAssetDetail,
  MediaAssetPage,
  MediaAssetPatch,
  MediaBulkTagDto,
  MediaCollectionResponse,
  MediaFromUrlDto,
  MediaUploadResponse,
  ReprocessStatus,
  ResolutionReportRow,
  UpdateMediaCollectionDto,
} from '@brickup/shared/dto'
import type { ImageCropMode } from '@brickup/shared/enums'
import { apiClient, publicClient } from './client'
import type { GlanceOptions } from './glance'
import {
  IMAGE_USAGES_KEY,
  MEDIA_ASSET_KEY,
  MEDIA_ASSETS_KEY,
  MEDIA_ASSETS_LIST_KEY,
  MEDIA_ASSETS_TAGS_KEY,
  MEDIA_COLLECTIONS_KEY,
  MEDIA_REPROCESS_KEY,
} from './query-keys'
import { useMutationWithInvalidation } from '@/hooks/use-mutation-with-invalidation'

const BASE = '/media-assets'

export type MediaLibraryFilter = {
  search?: string
  tags?: string[]
  tagMode?: 'AND' | 'OR'
  format?: string
  unusedOnly?: boolean
  minWidth?: number
  minHeight?: number
  createdAfter?: string
  sort?: MediaSort
}

/** The library is unbounded, so it is paged by cursor: a new upload arriving
 *  mid-scroll must not shift a page boundary under the reader. */
export function useMediaAssets(
  filter: MediaLibraryFilter,
  /** Off while the surface asking is shut. A picker that fetches the library
   *  behind its own closed dialog costs the page a full first page of assets
   *  and their renditions for a button nobody pressed. */
  options: { enabled?: boolean } = {},
) {
  return useInfiniteQuery({
    enabled: options.enabled ?? true,
    queryKey: [...MEDIA_ASSETS_LIST_KEY, filter],
    initialPageParam: null as number | null,
    queryFn: async ({ pageParam }): Promise<MediaAssetPage> => {
      const { data } = await apiClient.get<MediaAssetPage>(BASE, {
        params: {
          ...filter,
          tags: filter.tags?.length ? filter.tags.join(',') : undefined,
          unusedOnly: filter.unusedOnly ? 'true' : undefined,
          ...(pageParam ? { cursor: pageParam } : {}),
        },
      })
      return data
    },
    getNextPageParam: (page: MediaAssetPage) => page.nextCursor,
  })
}

/** How often an asset waiting on its derivatives asks again. */
const RENDER_POLL_MS = 2_000

/**
 * How long a wait on a queued render lasts before a screen lets go.
 *
 * A render is seconds; past a minute the queue is not working through it — no
 * worker, or the job spent its attempts — and asking every two seconds for
 * ever answers nothing. Read by whatever draws the wait, so the polling and
 * what the operator is told end together.
 */
export const RENDER_PATIENCE_MS = 60_000

export function useMediaAsset(id: number | null) {
  // Stamped when a render starts and cleared when it ends: the wait is a
  // deadline, and a tally of updates counts every refetch the query made for
  // its own reasons.
  const renderStartedAt = useRef<number | null>(null)

  return useQuery({
    queryKey: MEDIA_ASSET_KEY(id),
    enabled: id != null,
    queryFn: async (): Promise<MediaAssetDetail> => {
      const { data } = await apiClient.get<MediaAssetDetail>(`${BASE}/${id}`)
      return data
    },
    // A correction queues the render and returns at once. Polling is what
    // turns the new files up on the screen that asked for them; it stops the
    // moment the job leaves the queue, and gives up on a queue that is plainly
    // not answering.
    refetchInterval: query => {
      if (!query.state.data?.rendering) {
        renderStartedAt.current = null
        return false
      }
      renderStartedAt.current ??= Date.now()
      return Date.now() - renderStartedAt.current < RENDER_PATIENCE_MS
        ? RENDER_POLL_MS
        : false
    },
  })
}

/** Named library filters, shared across the staff. */
export function useMediaCollections() {
  return useQuery({
    queryKey: MEDIA_COLLECTIONS_KEY,
    queryFn: async (): Promise<MediaCollectionResponse[]> => {
      const { data } = await apiClient.get<MediaCollectionResponse[]>(
        `${BASE}/collections`,
      )
      return data
    },
  })
}

export function useCreateMediaCollection() {
  return useMutationWithInvalidation(
    (dto: CreateMediaCollectionDto) =>
      apiClient.post(`${BASE}/collections`, dto),
    [MEDIA_COLLECTIONS_KEY],
  )
}

export function usePatchMediaCollection() {
  return useMutationWithInvalidation(
    ({ id, dto }: { id: number; dto: UpdateMediaCollectionDto }) =>
      apiClient.patch(`${BASE}/collections/${id}`, dto),
    [MEDIA_COLLECTIONS_KEY],
  )
}

export function useDeleteMediaCollection() {
  return useMutationWithInvalidation(
    (id: number) => apiClient.delete(`${BASE}/collections/${id}`),
    [MEDIA_COLLECTIONS_KEY],
  )
}

export function useMediaTags() {
  return useQuery({
    queryKey: MEDIA_ASSETS_TAGS_KEY,
    queryFn: async (): Promise<Array<{ tag: string; count: number }>> => {
      const { data } = await apiClient.get(`${BASE}/tags`)
      return data
    },
  })
}

/**
 * Downloads the selection as one zip.
 *
 * A blob response: browsers block the second and later downloads a page
 * triggers, so a link per file would deliver one of ten.
 */
export function useDownloadMediaAssets() {
  return useMutation({
    mutationFn: async (ids: number[]) => {
      const { data } = await apiClient.post<Blob>(
        `${BASE}/download`,
        { ids },
        { responseType: 'blob' },
      )

      const url = URL.createObjectURL(data)
      const link = document.createElement('a')
      link.href = url
      link.download = 'bilder.zip'
      link.click()
      // Revoked on the next frame: revoking synchronously races the click the
      // browser has not acted on yet.
      requestAnimationFrame(() => URL.revokeObjectURL(url))
    },
  })
}

/**
 * Uploads one image, taking the shortest path for its size.
 *
 * A small file goes through the API, where multer already checks it. A large
 * one is PUT straight to S3 and only then registered — the request never
 * occupies a worker, which is what makes a 60 MB original possible at all.
 */
/** How far the bytes of one file have got, as a fraction between 0 and 1. */
export type UploadProgress = (fraction: number) => void

export async function uploadMediaAsset(
  file: File,
  meta: { title?: string; tags?: string[] },
  onProgress?: UploadProgress,
): Promise<MediaUploadResponse> {
  if (file.size <= IMAGE_DIRECT_UPLOAD_THRESHOLD_BYTES) {
    const form = new FormData()
    form.append('file', file)
    if (meta.title) form.append('title', meta.title)
    for (const tag of meta.tags ?? []) form.append('tags', tag)

    const { data } = await apiClient.post<MediaUploadResponse>(BASE, form, {
      onUploadProgress: event =>
        onProgress?.(event.total ? event.loaded / event.total : 0),
    })
    return data
  }

  const { data: staged } = await apiClient.post<{
    uploadUrl: string
    s3Key: string
  }>(`${BASE}/upload-url`, { filename: file.name, mimeType: file.type })

  await putWithProgress(staged.uploadUrl, file, onProgress)

  const { data } = await apiClient.post<MediaUploadResponse>(
    `${BASE}/finalize-upload`,
    { s3Key: staged.s3Key, title: meta.title, tags: meta.tags },
  )
  return data
}

/**
 * The pre-signed PUT, over `XMLHttpRequest` rather than `fetch`.
 *
 * `fetch` reports no upload progress in any browser, and this is the path the
 * large files take — so the set-level counter sat at "0 von 1" for the whole
 * of a 90 MB upload, which is the case a progress bar exists for. XHR's
 * `upload.onprogress` is the only browser API that reports it.
 *
 * The type is the one that was signed; a different one is refused by the
 * signature.
 */
function putWithProgress(
  url: string,
  file: File,
  onProgress?: UploadProgress,
): Promise<void> {
  return new Promise((resolve, reject) => {
    const request = new XMLHttpRequest()
    request.open('PUT', url)
    request.setRequestHeader('Content-Type', file.type)

    request.upload.onprogress = event => {
      if (event.lengthComputable) onProgress?.(event.loaded / event.total)
    }
    request.onload = () =>
      request.status >= 200 && request.status < 300
        ? resolve()
        : reject(new Error(`Upload to storage failed: ${request.status}`))
    request.onerror = () => reject(new Error('Upload to storage failed'))
    request.onabort = () => reject(new Error('Upload to storage was aborted'))

    request.send(file)
  })
}

export function useUploadMediaAssetFromUrl() {
  return useMutationWithInvalidation(
    async (dto: MediaFromUrlDto) => {
      const { data } = await apiClient.post<MediaUploadResponse>(
        `${BASE}/from-url`,
        dto,
      )
      return data
    },
    [MEDIA_ASSETS_KEY, MEDIA_ASSETS_TAGS_KEY, MEDIA_REPROCESS_KEY],
  )
}

export function useUploadMediaAsset() {
  return useMutationWithInvalidation(
    async ({
      file,
      title,
      tags,
      onProgress,
    }: {
      file: File
      title?: string
      tags?: string[]
      onProgress?: UploadProgress
    }) => {
      return uploadMediaAsset(file, { title, tags }, onProgress)
    },
    [MEDIA_ASSETS_KEY, MEDIA_ASSETS_TAGS_KEY, MEDIA_REPROCESS_KEY],
  )
}

/** Puts a new file behind an existing asset, keeping every link to it. */
export function useReplaceMediaAsset() {
  return useMutationWithInvalidation(
    async ({ id, file }: { id: number; file: File }) => {
      const form = new FormData()
      form.append('file', file)
      const { data } = await apiClient.post<MediaAssetDetail>(
        `${BASE}/${id}/replace`,
        form,
      )
      return data
    },
    [MEDIA_ASSETS_KEY],
  )
}

export function usePatchMediaAsset() {
  const queryClient = useQueryClient()

  return useMutationWithInvalidation(
    async ({ id, dto }: { id: number; dto: MediaAssetPatch }) => {
      const { data } = await apiClient.patch<MediaAssetDetail>(
        `${BASE}/${id}`,
        dto,
      )
      return data
    },
    // Decided per field below: a rotate, a crop and a focal press come
    // through here too, and invalidating the prefix for one of those refetches
    // every page the reader has scrolled through.
    [],
    {
      // The endpoint answers with the whole detail it just wrote. Writing it
      // straight into the cache is what a refetch of the same row would cost:
      // a second read, its resolution warnings and a queue round trip per
      // correction, and it drops every other asset's cached detail with it.
      onSuccess: (asset, { id, dto }) => {
        queryClient.setQueryData(MEDIA_ASSET_KEY(id), asset)

        // A rotate, a crop and a focal press come through here as well and
        // change neither of the two below, which is why each asks first.
        //
        // The title and the tags are what the grid and the list draw beside
        // the picture; the title is also a sort order and part of what the
        // search matches, so a row can move or leave the filter — which only
        // a refetch gets right.
        if (dto.title !== undefined || dto.tags !== undefined) {
          queryClient.invalidateQueries({ queryKey: MEDIA_ASSETS_LIST_KEY })
        }
        // The tag list is a scan of every tagged asset.
        if (dto.tags !== undefined) {
          queryClient.invalidateQueries({ queryKey: MEDIA_ASSETS_TAGS_KEY })
        }
      },
    },
  )
}

export function useDeleteMediaAsset() {
  return useMutationWithInvalidation(
    (id: number) => apiClient.delete(`${BASE}/${id}`),
    [MEDIA_ASSETS_KEY, MEDIA_ASSETS_TAGS_KEY],
  )
}

export function useBulkTagMediaAssets() {
  return useMutationWithInvalidation(
    async (dto: MediaBulkTagDto) => {
      const { data } = await apiClient.patch(`${BASE}/bulk/tags`, dto)
      return data
    },
    [MEDIA_ASSETS_KEY, MEDIA_ASSETS_TAGS_KEY],
  )
}

export function useBulkDeleteMediaAssets() {
  return useMutationWithInvalidation(
    async (ids: number[]) => {
      const { data } = await apiClient.post<{
        deleted: number[]
        blocked: number[]
      }>(`${BASE}/bulk/delete`, { ids })
      return data
    },
    [MEDIA_ASSETS_KEY, MEDIA_ASSETS_TAGS_KEY],
  )
}

// ─── Usages and reprocessing ─────────────────────────────────────────

/**
 * The rendering geometry, on the route that does not require staff.
 *
 * Every surface that draws an image reads this — including a logged-in
 * customer's configurator and the anonymous public one. The admin route
 * answers 403 and 401 there, which left `usages` empty and every `UsageImage`
 * silently on the legacy size scheme.
 *
 * `publicClient`, not `apiClient`: the authenticated client's request
 * interceptor rejects with a synthetic 401 *without sending anything* when
 * there is no token, so on the public configurator the query never reached
 * the route at all — making the endpoint public fixed the server and left the
 * caller unable to ask.
 */
export function useImageUsageGeometry() {
  return useQuery({
    queryKey: [...IMAGE_USAGES_KEY, 'geometry'],
    staleTime: 5 * 60_000,
    queryFn: async (): Promise<ImageUsageGeometry[]> => {
      const { data } = await publicClient.get<ImageUsageGeometry[]>(
        `${BASE}/usages/geometry`,
      )
      return data
    },
  })
}

export function useImageUsages() {
  return useQuery({
    queryKey: IMAGE_USAGES_KEY,
    // Read by every image on screen and changed only from the system area.
    staleTime: 5 * 60_000,
    queryFn: async (): Promise<ImageUsageResponse[]> => {
      const { data } = await apiClient.get<ImageUsageResponse[]>(
        `${BASE}/usages`,
      )
      return data
    },
  })
}

export type UsageInput = {
  key?: string
  label?: string
  targetWidth?: number
  aspectWidth?: number | null
  aspectHeight?: number | null
  cropMode?: ImageCropMode
  densities?: number[]
  sortOrder?: number
}

/**
 * What both writes to a usage do with what they get back.
 *
 * Either endpoint answers with the whole list, freshly aggregated. Writing it
 * into the cache is what a refetch would cost: a second pass of the derivative
 * count over the whole table, and the settings screen commits one write per
 * control. Only the geometry is invalidated — every `UsageImage` on screen
 * reads that, and the answer does not carry it.
 */
function useUsageListWrite() {
  const queryClient = useQueryClient()

  return {
    onSuccess: (usages: ImageUsageResponse[]) => {
      queryClient.setQueryData(IMAGE_USAGES_KEY, usages)
      queryClient.invalidateQueries({
        queryKey: [...IMAGE_USAGES_KEY, 'geometry'],
      })
    },
  }
}

export function useCreateImageUsage() {
  return useMutationWithInvalidation(
    async (dto: UsageInput) => {
      const { data } = await apiClient.post<ImageUsageResponse[]>(
        `${BASE}/usages`,
        dto,
      )
      return data
    },
    [MEDIA_REPROCESS_KEY],
    useUsageListWrite(),
  )
}

export function usePatchImageUsage() {
  return useMutationWithInvalidation(
    async ({ id, dto }: { id: number; dto: UsageInput }) => {
      const { data } = await apiClient.patch<ImageUsageResponse[]>(
        `${BASE}/usages/${id}`,
        dto,
      )
      return data
    },
    [MEDIA_REPROCESS_KEY],
    useUsageListWrite(),
  )
}

export function useDeleteImageUsage() {
  return useMutationWithInvalidation(
    (id: number) => apiClient.delete(`${BASE}/usages/${id}`),
    [IMAGE_USAGES_KEY, MEDIA_REPROCESS_KEY],
  )
}

/** Polls while the queue is working, so the progress bar moves on its own. */
export function useReprocessStatus({
  refetchInterval = false,
  ...options
}: GlanceOptions = {}) {
  return useQuery({
    queryKey: MEDIA_REPROCESS_KEY,
    queryFn: async (): Promise<ReprocessStatus> => {
      const { data } = await apiClient.get<ReprocessStatus>(`${BASE}/reprocess`)
      return data
    },
    ...options,
    // A queue that is working reports itself; idle, it is the caller's
    // cadence, which for a glance is none at all.
    refetchInterval: query =>
      query.state.data?.running || (query.state.data?.pending ?? 0) > 0
        ? 3000
        : refetchInterval,
  })
}

export function useStartReprocess() {
  return useMutationWithInvalidation(async () => {
    const { data } = await apiClient.post<{ queued: number }>(
      `${BASE}/reprocess`,
    )
    return data
  }, [MEDIA_REPROCESS_KEY])
}

/** Crosses every usage with every asset it names, so a result is kept for a
 *  few minutes; a change to the library invalidates it with the rest. */
export function useResolutionReport(options?: GlanceOptions) {
  return useQuery({
    ...options,
    // The cross is expensive, so the keep is the hook's and not the caller's:
    // written past the spread, no call site can shorten it.
    staleTime: 5 * 60_000,
    refetchInterval: false,
    queryKey: [...MEDIA_ASSETS_KEY, 'resolution-report'],
    queryFn: async (): Promise<ResolutionReportRow[]> => {
      const { data } = await apiClient.get<ResolutionReportRow[]>(
        `${BASE}/resolution-report`,
      )
      return data
    },
  })
}

/**
 * Where an asset's image is served from, addressed rather than pointed at S3.
 *
 * A note editor persists this URL inside the stored document, where no later
 * render can redirect it — so it is the api module's to move, not a caller's
 * to spell.
 */
export function mediaAssetImageUrl(uuid: string): string {
  return `${import.meta.env.VITE_API_URL}${BASE}/image/${uuid}`
}
